import { useState, useRef, useEffect, useMemo } from "react";
import axios from "axios";
import Field from "components/field";
import SafeArea from "components/safe-area";
import Spinner from "components/spinner";
import { useTable } from "components/table";
import PaymentsList from "containers/payments";
import useQueryString from "hooks/useQueryString";
import { useQuery } from "react-query";
import { TPayment } from "types/payment";
import Placeholder from "views/request/component/placeholder";
import { useTranslation } from 'react-i18next';
import { ContainerWithSidebar } from "components/layout";
import PaymentFilter from "./paymentFilter";
import { useHistory } from "react-router-dom";
import QueryString, { type ParsedQuery } from "query-string";
import { debounce } from "lodash";
import {
  PAYMENTS_QUERY_OPTS,
  parsePaymentsSearchToRecord,
  searchesEquivalent,
  buildPaymentListRequestParams,
} from "utils/canonicalQueryString";
import { Query, Count } from "./style-and-types";

const paymentsStringifyOptions = PAYMENTS_QUERY_OPTS.stringify;
const SEARCH_DEBOUNCE_MS = 250;


export default function Payments(props: any) {
  const { t } = useTranslation();
  const tBase = "views.payments.index";
  const tr = (key: string) => t(`${tBase}.${key}`);
  const [initialLoad, setInitialLoad] = useState(true);
  const { location } = props;
  const [counts, setCounts] = useState<Count[] | null>(null); // Store counts locally
  const history = useHistory()

  // Single source of truth from the URL on every render (no empty {} on first paint).
  const queryFilter: Query = useMemo(
    () => parsePaymentsSearchToRecord(location.search),
    [location.search]
  );

  const search = useQueryString<any>({
    parseOptions: PAYMENTS_QUERY_OPTS.parse,
    stringifyOptions: paymentsStringifyOptions,
    replace: true,
    compareSearch: searchesEquivalent,
  });

  const table = useTable()

  const urlSearchValue = queryFilter.search?.[0] || "";
  const [searchValue, setSearchValue] = useState<string>(urlSearchValue);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [isSearchFocused, setIsSearchFocused] = useState(false);

  const queryFilterRef = useRef(queryFilter);
  queryFilterRef.current = queryFilter;

  useEffect(() => {
    // Avoid resetting caret while user is typing; still sync when navigation changes URL.
    if (isSearchFocused) return;
    setSearchValue(urlSearchValue);
  }, [urlSearchValue, isSearchFocused]);

  const monthMap: Record<string, number> = {
    jan: 1,
    january: 1,
    ene: 1,
    enero: 1,
    feb: 2,
    february: 2,
    febr: 2,
    febrero: 2,
    mar: 3,
    march: 3,
    marzo: 3,
    apr: 4,
    april: 4,
    abr: 4,
    abril: 4,
    may: 5,
    mayo: 5,
    jun: 6,
    june: 6,
    junio: 6,
    jul: 7,
    july: 7,
    julio: 7,
    aug: 8,
    august: 8,
    ago: 8,
    agosto: 8,
    sep: 9,
    sept: 9,
    september: 9,
    septiembre: 9,
    oct: 10,
    october: 10,
    octubre: 10,
    nov: 11,
    november: 11,
    noviembre: 11,
    dec: 12,
    december: 12,
    dic: 12,
    diciembre: 12,
  };

  const pad2 = (n: number) => String(n).padStart(2, "0");
  const lastDayOfMonth = (year: number, month1Based: number) =>
    new Date(year, month1Based, 0).getDate();

  const applyCurrencySearchToApiParams = (
    params: Record<string, string>,
    rawSearch: unknown
  ) => {
    if (rawSearch == null) return;
    const s = String(rawSearch).trim();
    if (!s) return;

    // IMPORTANT: digits-only searches are usually Payment IDs on this screen.
    // Don't convert them into amount ranges (that would clear `search` and break ID lookup).
    if (/^\d+$/.test(s)) return;

    // If user typed a currency/number, prefer an exact amount match via filters
    // instead of the broad backend string search (which can match e.g. "105" when searching "1.5").
    const currencyLike = s.match(/^\$?\s*-?\d[\d,]*(?:\.\d{1,2})?\s*$/);
    if (!currencyLike) return;

    const normalized = s.replace(/[$,\s]/g, "");
    const neg = normalized.startsWith("-");
    const abs = neg ? normalized.slice(1) : normalized;
    const [intPartRaw, fracRaw = ""] = abs.split(".");
    const intPart = intPartRaw.replace(/^0+(?=\d)/, "") || "0";
    const frac = fracRaw.slice(0, 2);
    const fracLen = fracRaw.length;

    // Convert to integer cents, avoiding float issues.
    const toCents = (i: string, f: string) =>
      Number(i) * 100 + Number((f + "00").slice(0, 2));
    const baseCents = toCents(intPart, frac);
    if (!Number.isFinite(baseCents)) return;

    // Range behavior:
    // - "$1"   => 1.00 - 1.99
    // - "$1.5" => 1.50 - 1.59
    // - "$1.50"=> exact 1.50
    let minCents = baseCents;
    let maxCents = baseCents;
    if (fracLen === 0) {
      maxCents = baseCents + 99;
    } else if (fracLen === 1) {
      maxCents = baseCents + 9;
    }

    const fmt = (cents: number) => {
      const sign = neg ? "-" : "";
      const absCents = Math.abs(cents);
      const dollars = Math.floor(absCents / 100);
      const centsPart = String(absCents % 100).padStart(2, "0");
      return `${sign}${dollars}.${centsPart}`;
    };

    params.amountMin = fmt(minCents);
    params.amountMax = fmt(maxCents);
    params.search = "";
  };

  const applyDateSearchToApiParams = (
    params: Record<string, string>,
    rawSearch: unknown
  ) => {
    if (rawSearch == null) return;
    const s = String(rawSearch).trim();
    if (!s) return;

    const lower = s.toLowerCase();

    // Year only ("2026") => whole year range.
    const yearOnly = s.match(/^(\d{4})$/);
    if (yearOnly) {
      const year = Number(yearOnly[1]);
      if (Number.isFinite(year)) {
        params.beginDate = `${year}-01-01`;
        params.endDate = `${year}-12-31`;
        params.search = "";
        return;
      }
    }

    const clampDay = (year: number, month: number, day: number) => {
      const last = lastDayOfMonth(year, month);
      return day >= 1 && day <= last ? day : null;
    };

    // Month + day ("Apr 17" / "Apr17" / "Abr 17") with optional year ("Apr 17 2026").
    // Uses current year when omitted.
    const monthDay = lower.match(
      /^([a-záéíóúñ]+)\s*[-/.,]?\s*(\d{1,2})(?:\s*[,/-]?\s*(\d{4}))?$/
    );
    if (monthDay) {
      const m = monthMap[monthDay[1]];
      const year = monthDay[3] ? Number(monthDay[3]) : new Date().getFullYear();
      const day = Number(monthDay[2]);
      if (m && Number.isFinite(year) && Number.isFinite(day)) {
        const validDay = clampDay(year, m, day);
        if (validDay != null) {
          const mm = pad2(m);
          const dd = pad2(validDay);
          params.beginDate = `${year}-${mm}-${dd}`;
          params.endDate = `${year}-${mm}-${dd}`;
          params.search = "";
          return;
        }
      }
    }

    // Month only ("Apr"/"Abr") => use current year.
    const monthOnly = monthMap[lower];
    if (monthOnly) {
      const year = new Date().getFullYear();
      const mm = pad2(monthOnly);
      params.beginDate = `${year}-${mm}-01`;
      params.endDate = `${year}-${mm}-${pad2(lastDayOfMonth(year, monthOnly))}`;
      // Avoid AND-ing a text search that doesn't include entry_date on the backend.
      params.search = "";
      return;
    }

    // "Apr 2026" / "Abr 2026" etc => date range for that month in that year.
    const monthYear = lower.match(/^([a-záéíóúñ]+)\s+(\d{4})$/);
    if (monthYear) {
      const m = monthMap[monthYear[1]];
      const year = Number(monthYear[2]);
      if (m && Number.isFinite(year)) {
        const mm = pad2(m);
        params.beginDate = `${year}-${mm}-01`;
        params.endDate = `${year}-${mm}-${pad2(lastDayOfMonth(year, m))}`;
        params.search = "";
      }
    }
  };

  // Only sync sort into the URL when the user has an active sort. `table.sort` is always an
  // object (truthy), so `if (table.sort)` ran every time and fought filter URL updates → loops.
  useEffect(() => {
    if (table.sort?.column && table.sort?.order) {
      search.update({ ...search.search, ...table.sort });
    }
  }, [table.sort]);

  const query = useQuery(
    ["payments", location.search, queryFilter],
    async () =>
      await axios
        .get(
          `${process.env.REACT_APP_SERVER_URL}/api/v2/company/payments/payment-list`,
          {
            params: (() => {
              const params = buildPaymentListRequestParams(
                search.search as ParsedQuery<string>
              );
              applyCurrencySearchToApiParams(params, params.search);
              applyDateSearchToApiParams(params, params.search);
              return params;
            })(),
          }
        )
        .then((res) => {
          return res.data
        }),
    {
      keepPreviousData: true,
    }
  );

  useEffect(() => {
    const parsedString = parsePaymentsSearchToRecord(location.search);
    let queryPage = parsedString.page && +parsedString.page[0];
    if (queryPage !== table.pageIndex)
      table.handlePage((parsedString?.page && +parsedString?.page[0]) || 1);
    setInitialLoad(false);
  }, [location.search]);

  // After the first location sync, keep `page` in the URL aligned with table pagination.
  // initialLoad prevents the first pass from clobbering URL page before handlePage runs.
  useEffect(() => {
    if (initialLoad) return;

    const parsedString = parsePaymentsSearchToRecord(location.search);
    const nextPage = table.pageIndex.toString();
    const curPage = parsedString.page?.[0];
    if (curPage === nextPage) return;

    updateUrl({ ...parsedString, page: [nextPage] });
  }, [initialLoad, table.pageIndex]);

  const updateUrl = (qf: Query) => {
    const stringifyQuery: { [key: string]: string[] } = {};
    Object.entries(qf).forEach(([key, value]) => {
      const cleaned = value.map(String).filter((v) => v !== "");
      if (cleaned.length) stringifyQuery[key] = cleaned;
    });

    const qs = QueryString.stringify(stringifyQuery, paymentsStringifyOptions);
    const nextSearch = qs ? `?${qs}` : "";
    if (searchesEquivalent(location.search, nextSearch)) return;
    history.replace({ search: nextSearch });
  };

  const selected = useMemo(() => queryFilter?.category?.[0], [queryFilter]);

  useEffect(() => {
    if (query.data?.count) {
      // Update local counts only after successful data fetch
      setCounts(query.data.count);
    }
  }, [selected, query.data?.count]);    

  const listPageCount = useMemo(() => {
    const data = query.data as any;
    if (!data) return 1;
    const count = selected
      ? data.count?.find((c: any) => c.status === selected)?.count ?? 0
      : data.page_count ?? 0;
    const pages = Math.ceil(Number(count) / 50);
    return Number.isFinite(pages) && pages > 0 ? pages : 1;
  }, [query.data, selected]);

  const pushSearchToUrl = (raw: string) => {
    const trimmed = raw.trim();
    const next: Query = { ...(queryFilterRef.current || {}) };
    next.page = ["1"];
    if (!trimmed) {
      delete next.search;
    } else {
      next.search = [trimmed];
    }
    updateUrl(next);
  };

  const debouncedPushSearch = useMemo(
    () =>
      debounce((value: string) => pushSearchToUrl(value), SEARCH_DEBOUNCE_MS, {
        maxWait: 1000,
      }),
    []
  );

  useEffect(() => () => debouncedPushSearch.cancel(), [debouncedPushSearch]);

  const scheduleDebouncedSearch = (value: string) => {
    if (!value.trim()) {
      debouncedPushSearch.cancel();
      pushSearchToUrl("");
      return;
    }
    debouncedPushSearch(value);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchValue(value);
    if (!value.trim()) {
      debouncedPushSearch.cancel();
      pushSearchToUrl("");
      return;
    }
    const inputType = (e.nativeEvent as InputEvent).inputType;
    if (inputType === "insertFromPaste" || inputType === "insertFromDrop") {
      scheduleDebouncedSearch(value);
    }
  };

  const handleSearchKeyUp = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.nativeEvent.isComposing || e.key === "Process") return;
    scheduleDebouncedSearch(e.currentTarget.value);
  };

  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      debouncedPushSearch.cancel();
      pushSearchToUrl((e.target as HTMLInputElement).value);
    }
  };

  const handleSearchBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsSearchFocused(false);
    // Apply the latest value immediately when leaving the field.
    debouncedPushSearch.flush();
    scheduleDebouncedSearch(e.currentTarget.value);
  };

  const handleStatusClick = (status: string) => {
    if (status === "showAll") {
      updateUrl({});
      setSearchValue("");
      // resetDateFilter();
    } else {
      updateUrl({ ...queryFilter, category: [status], page: ["1"] });
    }
  };

  return (
    <ContainerWithSidebar>
      <PaymentFilter
        query={queryFilter}
        selected={selected}
        count={counts}
        categories={query.data?.categories}
        {...{ handleStatusClick, search }}
      />
      <div className="divide-y">
        <SafeArea>
          <div className="py-4">
            <h1>{tr("Payments")}</h1>
            <Field
              value={searchValue}
              onChange={handleSearchChange}
              onKeyUp={handleSearchKeyUp}
              onKeyDown={handleSearchKeyDown}
              onFocus={() => setIsSearchFocused(true)}
              onBlur={handleSearchBlur}
              ref={searchInputRef}
            />
          </div>
        </SafeArea>
        <SafeArea>
          <div className="py-4">
            {query.isLoading && !query.data ? (
              <Spinner />
            ) : query.data ? (
              <PaymentsList
                data={query.data.payments}
                type="all"
                table={{ ...table, pageCount: listPageCount }}
                fixedColumnWidths
              />
            ) : (
              <Placeholder
                type="payment"
                header={tr("No payments yet")}
                label={tr("Payments will appear here when added.")}
              />
            )}
          </div>
        </SafeArea>
      </div>
    </ContainerWithSidebar>
  );
}
